import React, { FC, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { SurveyStatsStatusEnum, SurveyTrendOverTimeDto } from '@families-link/shared';
import { AnalyticsChartColors } from '../../enums/analytics-chart-colors.enum';

export type SurveyTrendLineChartComponentProps = {
	data?: SurveyTrendOverTimeDto[];
};

type StatusKey = SurveyStatsStatusEnum.Completed | SurveyStatsStatusEnum.Delivered | SurveyStatsStatusEnum.Sent;

type ChartRow = {
	month: string;
	[SurveyStatsStatusEnum.Completed]: number;
	[SurveyStatsStatusEnum.Delivered]: number;
	[SurveyStatsStatusEnum.Sent]: number;
};

const STATUS_KEYS: StatusKey[] = [
	SurveyStatsStatusEnum.Completed,
	SurveyStatsStatusEnum.Delivered,
	SurveyStatsStatusEnum.Sent,
];

const STATUS_COLORS: Record<StatusKey, string> = {
	Completed: AnalyticsChartColors.Sky,
	Delivered: AnalyticsChartColors.SeaGreen,
	Sent: AnalyticsChartColors.Coral,
};

const formatMonthLabel = (monthStartIsoString: string, showYear: boolean) => {
	const date = new Date(monthStartIsoString);
	return date.toLocaleString('en', {
		month: 'short',
		...(showYear ? { year: 'numeric' } : {}),
	});
};

export const SurveyTrendLineChartComponent: FC<SurveyTrendLineChartComponentProps> = (props) => {
	const { data = [] } = props;
	const appTheme = useTheme();

	const { chartRows, showYear } = useMemo(() => {
		const years = new Set(data.map((item) => new Date(item._id.monthStart).getFullYear()));
		const shouldShowYear = years.size > 1;

		const totalsByMonthStart = new Map<string, number>();
		const countsByMonthStartAndStatus = new Map<string, Partial<Record<StatusKey, number>>>();

		for (const item of data) {
			const monthStart = item._id.monthStart;
			const lineDataKey = item._id.lineDataKey;
			const count = item.count ?? 0;

			if (lineDataKey === 'Total') {
				totalsByMonthStart.set(monthStart, count);
				continue;
			}

			if (STATUS_KEYS.includes(lineDataKey as StatusKey)) {
				const statusKey = lineDataKey as StatusKey;
				const existing = countsByMonthStartAndStatus.get(monthStart) ?? {};
				countsByMonthStartAndStatus.set(monthStart, { ...existing, [statusKey]: count });
			}
		}

		const uniqueMonthStarts = Array.from(new Set(data.map((item) => item._id.monthStart))).sort(
			(left, right) => new Date(left).getTime() - new Date(right).getTime()
		);

		const rows: ChartRow[] = uniqueMonthStarts.map((monthStart) => {
			const total = totalsByMonthStart.get(monthStart) ?? 0;
			const statusCounts = countsByMonthStartAndStatus.get(monthStart) ?? {};

			const toPercent = (value: number, totalValue: number) => {
				if (totalValue <= 0) return 0;
				return Math.round((value / totalValue) * 100);
			};

			return {
				month: formatMonthLabel(monthStart, shouldShowYear),
				Completed: toPercent(statusCounts.Completed ?? 0, total),
				Delivered: toPercent(statusCounts.Delivered ?? 0, total),
				Sent: toPercent(statusCounts.Sent ?? 0, total),
			};
		});

		return { chartRows: rows, showYear: shouldShowYear };
	}, [data]);

	const CustomLegend = (legendProps: any) => (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', mb: 2 }}>
			{legendProps.payload
				.filter((entry: any) => STATUS_KEYS.includes(entry.value as StatusKey))
				.map((entry: any) => (
					<Box key={entry.value} style={{ color: entry.color }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
							<Box
								sx={{
									height: 10,
									width: 10,
									borderRadius: '50%',
									backgroundColor: entry.color,
									flexShrink: 0,
								}}
							/>
							<Typography variant="body2" sx={{ color: '#797F92', fontSize: '14px' }}>
								{`${entry.value} %`}
							</Typography>
						</Box>
					</Box>
				))}
		</Box>
	);

	const CustomTooltip = (tooltipProps: any) => {
		const { active, payload, label } = tooltipProps;

		if (!active || !payload?.length) return null;

		const rowsToShow = payload.filter((p: any) => STATUS_KEYS.includes(p.name as StatusKey));

		return (
			<Box
				sx={{
					width: '200px',
					backgroundColor: 'white',
					border: '1px solid #ccc',
					borderRadius: '6px',
					padding: '10px',
					boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
					py: 2,
					px: 2,
				}}
			>
				<Typography sx={{ mb: '10px', fontWeight: 700 }}>{label}</Typography>

				{rowsToShow.map((entry: any) => (
					<Box
						key={entry.name}
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							gap: '8px',
							marginBottom: '6px',
						}}
					>
						<Box component="span" sx={{ flex: 1, color: entry.color }} title={entry.name}>
							{entry.name}
						</Box>
						<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
							{`${entry.value}%`}
						</Box>
					</Box>
				))}
			</Box>
		);
	};

	return (
		<ResponsiveContainer width="100%" height="100%">
			<LineChart data={chartRows.length > 0 ? chartRows : [{ month: '', Completed: 0, Delivered: 0, Sent: 0 }]}>
				<CartesianGrid strokeDasharray="3 3" syncWithTicks />

				<XAxis
					dataKey="month"
					padding={{ left: 20, right: 20 }}
					tick={({ x, y, payload, ...rest }) => (
						<text x={x} y={y + 10} textAnchor="middle" {...rest}>
							<tspan fontSize={12}>{payload.value}</tspan>
						</text>
					)}
				/>

				<YAxis
					domain={[0, 100]}
					allowDecimals={false}
					tickFormatter={(value) => `${value}%`}
					tick={({ x, y, payload, ...rest }) => (
						<text x={x} y={y + 5} textAnchor="end" {...rest} fill={appTheme.colors.grayLightBlue} fontWeight={500}>
							<tspan fontSize={12}>{`${payload.value}%`}</tspan>
						</text>
					)}
					tickSize={16}
				/>

				<Tooltip content={CustomTooltip} />
				<Legend content={<CustomLegend />} verticalAlign="top" align="right" />

				{STATUS_KEYS.map((statusKey) => (
					<Line
						key={statusKey}
						type="linear"
						dataKey={statusKey}
						stroke={STATUS_COLORS[statusKey]}
						strokeWidth={2}
						dot={false}
						activeDot={{ r: 5 }}
						isAnimationActive={false}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
};
